import { Navigate, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

/**
 * /too-short — shown when the interview ended below the 30s pre-filter.
 * Was `/fail` in the prototype. Allows a retry. Guards against direct
 * navigation by requiring sessionStorage.interview_failed to be set.
 */
export function TooShort() {
  const navigate = useNavigate();
  // Match the prototype's "ready" gate so a direct visit silently bounces
  // back to /interview instead of flashing this UI.
  const [ready, setReady] = useState<boolean | null>(null);

  useEffect(() => {
    const failed = sessionStorage.getItem("interview_failed");
    setReady(failed ? true : false);
  }, []);

  if (ready === null) return null;
  if (!ready) return <Navigate to="/interview" replace />;

  function retry() {
    // Clear the flag so a subsequent successful interview doesn't leave
    // a stale "you failed" marker behind, and a future direct visit
    // here bounces away as expected.
    sessionStorage.removeItem("interview_failed");
    // Return to /interview WITH the original entry params (studyId / d blob),
    // saved when the interview first loaded — a bare /interview has no study
    // id and errors with "Missing study id".
    const search = sessionStorage.getItem("interview_entry_search");
    navigate(search ? `/interview?${search}` : "/interview");
  }

  return (
    <div className="w-full max-w-[560px]">
      <h1 className="font-serif text-balance text-[26px] leading-[32px] sm:text-[32px] sm:leading-[40px] lg:text-[38px] lg:leading-[46px] text-center text-surface-ink mb-3 sm:mb-4 mx-auto max-w-[20ch]">
        That ended too soon
      </h1>
      <p className="text-center text-body-m text-surface-subtle mb-7 sm:mb-9 mx-auto max-w-[44ch] leading-relaxed">
        Your conversation was too short to save as complete. Lea is ready
        whenever you'd like to try again.
      </p>

      <div className="bg-surface rounded-card border border-brand-100 shadow-card p-6 sm:p-7 flex flex-col items-center text-center max-w-[440px] mx-auto">
        <p className="text-body-m text-surface-ink mb-5">
          Pick a quiet moment and give it another go.
        </p>
        <button
          type="button"
          onClick={retry}
          // Same sizing as the interview Start button so the two CTAs
          // feel like the same button at different moments.
          className="w-full sm:w-auto sm:min-w-[200px] inline-flex items-center justify-center gap-2 rounded-pill bg-brand-700 text-white min-h-[48px] px-7 text-body-m font-medium hover:bg-brand-800 active:bg-brand-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface transition-colors"
        >
          <span>Do again</span>
          {/* Circular arrow — signals "start over" rather than "continue". */}
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
            <path
              d="M13 8a5 5 0 1 1-1.5-3.5M13 2v3.5h-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
